import { AsyncPipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import type { STAFFINTERFACE, TENANT_MODEL } from '@fitness-force/models';
import { ErrorsComponent } from '@fitness-force/errors';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@fitness-force/environments';
import { ToastMessageService, StaffService } from '@fitness-force/api';

@Component({
  selector: 'change-tenant-access',
  templateUrl: './change-tenant-access.component.html',
  styleUrls: ['./change-tenant-access.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    StyleClassModule,
    MultiSelectModule,
    ErrorsComponent,
    TranslateModule,
    AsyncPipe,
  ],
})
export class ChangeTenantAccessComponent implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly staffService = inject(StaffService);
  private readonly toastMessageService = inject(ToastMessageService);

  readonly changeTenantAccesDialog = input<boolean>(false, {
    alias: 'visible',
  });
  readonly staff = input<STAFFINTERFACE>({} as STAFFINTERFACE);
  readonly closePopup = output({ alias: 'closePopup' });

  accessTenant$ = new BehaviorSubject([] as TENANT_MODEL[]);
  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  showErrorPopup = new BehaviorSubject(false);
  errorMessages = [] as any;

  constructor() {
    this.createForm();
  }

  ngOnInit(): void {
    this.ComponentInit();
  }

  createForm(): void {
    this.formGroup = this.fb.group({
      new_tenant_access: new UntypedFormControl([], [Validators.required]),
    });
  }

  ComponentInit() {
    this.store.select(COMPANY_INFO).subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);
    });

    this.store.select(TENANTS_INFO).subscribe((tenant) => {
      this.accessTenant$.next(tenant.accessed_tenant);

      const accesTenant: TENANT_MODEL[] = tenant.accessed_tenant.filter(
        (t: TENANT_MODEL) => {
          return this.staff().tenant_access_id.includes(t.id);
        },
      );

      this.formGroup.controls.new_tenant_access.setValue(accesTenant);
    });
  }

  ClosePopup(): void {
    this.closePopup.emit();
    this.formGroup.reset();
    this.closePopup.emit();
  }

  HideErrorPopup(event: any) {
    this.showErrorPopup?.next(false);
    this.errorMessages = [];
  }

  get NEWTENANTACCESSCONTROL(): UntypedFormControl {
    return this.formGroup.controls.new_tenant_access as UntypedFormControl;
  }

  get NEWTENANTACCESS(): number[] {
    const tenants = this.formGroup.controls.new_tenant_access.value;
    return tenants.map((t: any) => t.id);
  }

  ValidateAndSubmit(): void {
    if (this.formGroup.valid) {
      const staffId = this.staff().id;
      this.staffService
        .ChangeTenantAccess(environment.BASE_URL, staffId, this.NEWTENANTACCESS)
        .subscribe({
          next: () => {
            this.translate
              .get('common.message.record_updated')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.store.dispatch({ type: 'FETCH_STAFF_LIST' });
            this.ClosePopup();
          },
          error: (error: any) => {
            this.toastMessageService.showError(error);
          },
        });
    }
  }
}
